import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  Restaurant,
  RestaurantCreateFormData,
  RestaurantUpdateFormData,
} from "../../schemas/restaurantSchema";
import { useRestaurantsInfinite } from "../../hooks/useRestaurantsInfinite";
import RestaurantForm from "../forms/RestaurantForm";

const Restaurants: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<
    Restaurant | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    restaurants,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createRestaurant,
    updateRestaurant,
    deleteRestaurants,
  } = useRestaurantsInfinite({ search });

  const handleEdit = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (restaurant: Restaurant) => {
    if (window.confirm(`Delete Restaurant "${restaurant.name}"?`)) {
      await deleteRestaurants([restaurant.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} restaurant(s)?`,
      )
    ) {
      deleteRestaurants(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: RestaurantCreateFormData | RestaurantUpdateFormData,
  ) => {
    setIsSubmitting(true);
    try {
      if (selectedRestaurant?.id) {
        updateRestaurant(
          {
            id: selectedRestaurant.id,
            restaurant: data as RestaurantUpdateFormData,
          },
          {
            onSuccess: () => {
              setSelectedRestaurant(undefined);
              setIsFormModalOpen(false);
              setIsSubmitting(false);
            },
            onError: () => {
              setIsSubmitting(false);
            },
          },
        );
      } else {
        createRestaurant(data as RestaurantCreateFormData, {
          onSuccess: () => {
            setIsFormModalOpen(false);
            setIsSubmitting(false);
          },
          onError: () => {
            setIsSubmitting(false);
          },
        });
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<Restaurant>[] = [
    { accessorKey: "code", header: "Code" },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Restaurants</h1>
        <button
          onClick={() => {
            setSelectedRestaurant(undefined);
            setIsFormModalOpen(true);
          }}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={restaurants as Restaurant[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable
        getRowId={(restaurant) => restaurant.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <RestaurantForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedRestaurant(undefined);
          }}
          initialData={selectedRestaurant}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedRestaurant}
        />
      )}
    </>
  );
};

export default Restaurants;
